/**
 * Phase 4c — Modal "+ Ajouter au catalogue" one-click depuis une ligne hors-catalogue.
 *
 * Préremplie avec les infos de la ligne (label depuis description, unit, unit_price, vat_rate, section).
 * L'utilisateur peut compléter/corriger avant de POST.
 * Après création, on renvoie l'id au parent pour que la ligne soit re-marquée `catalog_item_id`.
 */
import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Plus } from "lucide-react";
import BENumericInput from "@/components/quote/BENumericInput";

export default function AddToCatalogModal({ open, line, onClose, onCreated, companyDefaultVat }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && line) {
      // Hotfix Phase 1 (2026-09-02) — Fallback strict sur `companyDefaultVat` (settings
      // société). JAMAIS de valeur hardcodée. `??` pour préserver le 0 (cocontractant).
      const lineVat = Number(line.vat_rate);
      const fallback = companyDefaultVat != null ? Number(companyDefaultVat) : null;
      const initialVat =
        [0, 6, 12, 21].includes(lineVat) ? lineVat : fallback;
      setForm({
        label: (line.description || "").slice(0, 120),
        pro_description: line.description || "",
        unit: line.unit || "unité",
        unit_price: line.unit_price || 0,
        default_vat_rate: initialVat,
        section: line.section || "Divers",
      });
    }
  }, [open, line, companyDefaultVat]);

  if (!open || !form) return null;
  if (form.default_vat_rate == null) {
    return null; // fail-fast : ne s'ouvre pas tant que settings société non chargés
  }

  const canSave = form.label.trim().length >= 2;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/catalog", form);
      toast.success("Poste ajouté au catalogue");
      onCreated?.(data);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="border-slate-800 bg-slate-900 text-slate-100"
        data-testid="add-to-catalog-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-slate-100">Ajouter au catalogue</DialogTitle>
          <DialogDescription className="text-slate-400">
            Une fois créé, ce poste sera réutilisable dans tous vos futurs devis (matching IA).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-slate-300 text-xs">Libellé court (visible dans la liste)</Label>
            <Input
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              className="border-slate-700 bg-slate-950/40 text-slate-100"
              placeholder="Ex : Démoussage toiture standard"
              data-testid="add-to-catalog-label"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-slate-300 text-xs">Description professionnelle</Label>
            <Textarea
              rows={3}
              value={form.pro_description}
              onChange={(e) => setForm({ ...form, pro_description: e.target.value })}
              className="border-slate-700 bg-slate-950/40 text-slate-100 text-sm"
              placeholder="Version détaillée reprise dans les nouveaux devis"
              data-testid="add-to-catalog-pro-description"
            />
          </div>
          <div className="grid grid-cols-4 gap-2">
            <div className="space-y-1 col-span-2">
              <Label className="text-slate-300 text-xs">Section</Label>
              <Input
                value={form.section}
                onChange={(e) => setForm({ ...form, section: e.target.value })}
                className="border-slate-700 bg-slate-950/40 text-slate-100"
                data-testid="add-to-catalog-section"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-slate-300 text-xs">Unité</Label>
              <Input
                value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}
                className="border-slate-700 bg-slate-950/40 text-slate-100"
                data-testid="add-to-catalog-unit"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-slate-300 text-xs">PU (€)</Label>
              <BENumericInput
                value={form.unit_price}
                onChange={(n) => setForm({ ...form, unit_price: n })}
                className="border-slate-700 bg-slate-950/40 text-slate-100 tabular"
                data-testid="add-to-catalog-unit-price"
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-slate-300 text-xs">TVA par défaut</Label>
            <Select
              value={String(form.default_vat_rate)}
              onValueChange={(v) => setForm({ ...form, default_vat_rate: parseInt(v, 10) })}
            >
              <SelectTrigger
                className="border-slate-700 bg-slate-950/40 text-slate-100"
                data-testid="add-to-catalog-vat"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-slate-700 bg-slate-900">
                <SelectItem value="6">6 % — Rénovation habitation privée &gt;10 ans</SelectItem>
                <SelectItem value="21">21 % — Standard</SelectItem>
                <SelectItem value="0">0 % — Cocontractant</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            className="border-slate-700 bg-slate-800/60 text-slate-200"
          >
            Annuler
          </Button>
          <Button
            onClick={save}
            disabled={!canSave || saving}
            className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
            data-testid="add-to-catalog-save-btn"
          >
            {saving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Plus className="mr-2 h-4 w-4" />
            )}
            Ajouter au catalogue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
